import React from "react";
import {
	IFormFieldProps,
	EWidgets,
	ICustomFormItemProps
} from "@organisms/ZForm/interface";
import { getCurrencySettings } from "@/Utils/Currency";
import { PricingOptions, ProductType } from "@/Contracts/Products";
import {
	handleMarkupMargin,
	handleMarkupChange,
	handleMarginChange
} from "@/Components/Utils/PricingCalculator";
import ZTypography from "@atoms/ZTypography";
import ZButton from "@atoms/ZButton";
import ZIcon, { Icons } from "@atoms/ZIcon";
import ZInputNumber, {
	InputNumberWidths
} from "@atoms/ZInputNumber";
import { IRenderCostPriceInputGroupProps } from "./interface";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";
import { useComponentDidUpdate } from "@/Hooks";
import ZSupplierSelect from "@organisms/ZSupplierSelect";
import { useFeatureFlag } from "@/Hooks/useFeature";

const { ZText } = ZTypography;

export default ({
	form,
	getFieldValues,
	bundledProductList,
	setMarginAndAmount,
	currentProductType,
	drawerState,
	resetSelectedDistributor,
	showDynamicPricingSection,
	selectedDistributor,
	suppliers,
	showAddSupplierDrawer,
	pricingDisabled,
	isBundleEnhancementEnable,
	isAmazonProduct
}: IRenderCostPriceInputGroupProps): IFormFieldProps[] => {
	const { getFieldValue } = form;

	const costInputRef = React.useRef<any>(null);

	const type =
		drawerState === EProductDrawerState.BUNDLECHILDEDIT
			? currentProductType
			: form.getFieldValue("type");
	const isBundleProduct = type === ProductType.Bundle;

	const onMarkupChange = (newMarkup?: number | string | null): void => {
		if (
			newMarkup === undefined ||
			form.getFieldValue("selected_cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();
		form.setFieldsValue(handleMarkupChange(values, Number(newMarkup)));
		setMarginAndAmount();
	};

	const updateCostPriceFromBundleItems = () => {
		const costPrice =
			bundledProductList?.reduce(
				(total, curr) =>
					curr.is_optional
						? total
						: total +
						  (curr.selected_cost_price || 0) * curr.quantity,
				0
			) || 0;

		form.setFieldsValue({ selected_cost_price: costPrice });
		setMarginAndAmount();
	};

	const onCostPriceChange = (
		newCostPrice?: number | string | null,
		shouldResetDistributor = true
	): void => {
		if (newCostPrice === undefined || newCostPrice === null) {
			return;
		}
		const values = getFieldValues();
		const calculatedValues = handleMarkupMargin(
			Number(newCostPrice),
			values.sell_price,
			values.quantity,
			values.should_add_setup_price_to_margin,
			values.selected_setup_price
		);
		
		// Batch all updates in a single call to prevent multiple re-renders that cause focus loss
		form.setFieldsValue({
			...calculatedValues,
			selected_cost_price: Number(newCostPrice)
		});
		setMarginAndAmount();
		shouldResetDistributor && resetSelectedDistributor();
	};

	const onMarginChange = (newMargin?: number | string | null): void => {
		if (
			newMargin === undefined ||
			newMargin === null ||
			form.getFieldValue("selected_cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();
		if (
			Number(newMargin) >= 100 &&
			Number(getFieldValues().selected_cost_price) !== 0
		) {
			return;
		}
		form.setFieldsValue(handleMarginChange(values, Number(newMargin)));
		setMarginAndAmount();
	};

	const handleOnFocusForCostPrice = (
		e: React.FocusEvent<HTMLInputElement>,
		newCostPrice?: number | string | null
	) => {
		if (
			newCostPrice === undefined ||
			newCostPrice === null ||
			selectedDistributor !== PricingOptions.Custom
		)
			return;
		e.target.type = "text";
		e.target?.setSelectionRange?.(
			e.target.value.indexOf("."),
			e.target.value.indexOf(".")
		);
		e.target.type = "number";
		onCostPriceChange(newCostPrice.toString(), false);
	};

	useComponentDidUpdate(() => {
		if (selectedDistributor === PricingOptions.Custom) {
			costInputRef.current.focus();
		}
	}, [selectedDistributor]);

	const CostPrice = ({ value, onChange: _onChange }: ICustomFormItemProps) => (
		<div className="flex items-center">
			<ZInputNumber
				type="number"
				step=".01"
				placeholder="Please enter product cost price"
				min={0}
				className="mr-2"
				width={InputNumberWidths.xl}
				value={value}
				onChange={v => {
					// Don't call onChange(v) here to avoid double form update
					// onCostPriceChange will handle the form update including the input value
					onCostPriceChange(Number(v));
				}}
				precision={2}
				addOnAfter={getCurrencySettings().currency}
				zref={costInputRef}
				onFocus={e => handleOnFocusForCostPrice(e, value)}
				disabled={
					(isBundleEnhancementEnable && pricingDisabled) ||
					isAmazonProduct
				}
			/>
			{getFieldValue("unit_of_measure") && (
				<ZText className="ml-2 whitespace-nowrap leading-8">
					{getFieldValue("unit_of_measure")}
				</ZText>
			)}
			{isBundleProduct && (
				<ZButton
					className="sub-action-button m-0"
					type="link"
					onClick={updateCostPriceFromBundleItems}
					disabled={isBundleEnhancementEnable && pricingDisabled}
				>
					<ZText className="text-blue-5 active:text-blue-6">
						<ZIcon
							name={Icons.COPY}
							size="sm"
							className="mx-1 stroke-current"
						/>
						Copy Product Total
					</ZText>
				</ZButton>
			)}
		</div>
	);
	const isAmazonDistributorEnabled = useFeatureFlag("amazon_distributor");

	const fields: IFormFieldProps[] = [
		{
			key: "selected_cost_price",
			label: "Cost Price",
			required: true,
			message: "Cost price cannot be empty",
			widget: CostPrice
		},
		{
			key: "markup",
			label: "Markup",
			widget: EWidgets.INPUTNUMBER,
			hasFeedback: true,
			disabled: isBundleEnhancementEnable && pricingDisabled,
			widgetProps: {
				placeholder: "Please enter product Markup",
				width: InputNumberWidths.lg,
				onChange: onMarkupChange,
				precision: 2,
				step: 0.1,
				addOnAfter: "%"
			}
		},
		{
			key: "margin",
			label: "Margin",
			widget: EWidgets.INPUTNUMBER,
			hasFeedback: true,
			disabled: isBundleEnhancementEnable && pricingDisabled,
			widgetProps: {
				placeholder: "Please enter product Margin",
				width: InputNumberWidths.lg,
				onChange: onMarginChange,
				precision: 2,
				step: 0.1,
				max: 100,
				addOnAfter: "%"
			},
			rules: [
				() => ({
					validator(_: any, value: any) {
						if (
							Number(value) >= 100 &&
							getFieldValue("selected_cost_price") > 0
						) {
							return Promise.reject(
								new Error(
									"Invalid Entry. Margin cannot be 100% unless cost price is 0"
								)
							);
						}
						return Promise.resolve();
					}
				})
			]
		}
	];

	if (
		type === ProductType.Hardware ||
		showDynamicPricingSection ||
		drawerState === EProductDrawerState.BUNDLECHILDEDIT
	) {
		const isDistributorSelected =
			selectedDistributor !== null &&
			selectedDistributor !== PricingOptions.Catalog &&
			selectedDistributor !== PricingOptions.Custom;

		const isDistributorForBundleChildEdit =
			drawerState === EProductDrawerState.BUNDLECHILDEDIT &&
			isDistributorSelected;

		const isEditOrEtilize =
			drawerState !== EProductDrawerState.ADD &&
			drawerState !== EProductDrawerState.BUNDLECHILDEDIT &&
			isDistributorSelected;

		const isCatalogOrCustom =
			isDistributorForBundleChildEdit ||
			isEditOrEtilize ||
			isAmazonProduct;

		if (isAmazonDistributorEnabled) {
			fields.unshift({
				key: "asin",
				label: "ASIN",
				hasFeedback: true,
				widget: EWidgets.INPUT,
				message: "Please enter ASIN",
				disabled: isCatalogOrCustom,
				widgetProps: {
					width: "full",
					placeholder: "Please enter ASIN"
				}
			});
		}

		fields.unshift(
			{
				key: "supplier_id",
				label: "Supplier Name",
				hasFeedback: true,
				widget: ({ value, onChange }: ICustomFormItemProps) => {
					return (
						<ZSupplierSelect
							onChange={onChange}
							placeholder="Please enter supplier name"
							defaultOptions={suppliers}
							value={value}
							disabled={isCatalogOrCustom}
							dropdownRender={menu => (
								<div>
									{menu}
									<div
										className="add-entity-action"
										onMouseDown={$event => {
											$event.preventDefault();
										}}
										onClick={showAddSupplierDrawer}
									>
										Add Supplier
									</div>
								</div>
							)}
						/>
					);
				},
				disabled: isCatalogOrCustom,
				message: "Please select supplier"
			},
			{
				key: "vendor_sku_no",
				label: "Supplier SKU No.",
				widget: EWidgets.INPUT,
				hasFeedback: true,
				disabled: isCatalogOrCustom,
				widgetProps: {
					width: "full",
					placeholder: "Please enter supplier SKU No"
				}
			}
		);
	}

	return fields;
};
